import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Account } from './models/account.model';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  httpClient = inject(HttpClient);

  private cacheName = 'video-cache';
  private s3BaseUrl = environment.s3BaseUrl;
  private accountData: Account | null = null;
  private readonly ACCOUNT_KEY = 'accountData';
  private readonly COMPANY_ID_KEY = 'companyId';
  private readonly COMPANY_NAME_KEY = 'companyName';

  constructor() {
    const stored = sessionStorage.getItem(this.ACCOUNT_KEY);

    if (stored) {
      try {
        this.accountData = JSON.parse(stored);
      } catch {
        this.accountData = null;
      }
    }
  }

  detectAppleDevice(): string {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    if (/iPhone/.test(ua)) {
      return 'iPhone';
    }

    if (/iPad/.test(ua)) {
      return 'iPad';
    }

    if (platform === 'MacIntel' && maxTouchPoints > 0) {
      return 'iPad';
    }

    if (/Macintosh/.test(ua)) {
      return 'Mac';
    }

    return 'Other';
  }

  getVideoFormat(): '.mp4' | '.mov' {
    const device = this.detectAppleDevice();

    if (device === 'iPhone' || device === 'iPad') {
      return '.mov';
    }

    return window.innerWidth >= 500 ? '.mp4' : '.mov';
  }

  async preloadVideos(videoUrls: string[]): Promise<void> {
    try {
      const cache = await caches.open(this.cacheName);

      await Promise.all(
        videoUrls.map(async (url) => {
          const response = await fetch(this.s3BaseUrl + url);
          if (response.ok) {
            const videoBlob = await response.blob();
            const videoResponse = new Response(videoBlob, {
              headers: { 'Content-Type': 'video/mp4' },
            });

            await cache.put(url, videoResponse);
          } else {
            console.error(`Failed to fetch video: ${url}`);
          }
        })
      );

    } catch (error) {
      console.error('Error preloading videos:', error);
    }
  }

  async getCachedVideo(url: string): Promise<Response | undefined> {
    const cache = await caches.open(this.cacheName);

    return cache.match(url);
  }

  async fetchAccountData(): Promise<Account | null> {
    const response = await lastValueFrom(this.httpClient.get<Account>('/api/Account/GetAccount'));
    this.accountData = response || null;
    sessionStorage.setItem(this.ACCOUNT_KEY, JSON.stringify(this.accountData));
    this.setCompanyName(this.accountData?.companyName || '');

    return this.accountData;
  }

  async fetchBookingtData(token: string): Promise<{ url: string }> {
    const response = await lastValueFrom(this.httpClient.get<{ url: string }>('/api/Booking/BookCall', {
      params: {
        token
      }
    }));

    return response;
  }

  async confirmEmailChange(token: string): Promise<{ content: string }> {
    const response = await lastValueFrom(this.httpClient.get<{ content: string }>('/api/ConfirmEmailChange/ConfirmEmailChange', {
      params: {
        token
      }
    }));

    return response;
  }

  getCompanyLogoUrl(blackText = false): string {
    const companyId = this.getCompanyId();
    const howdyCompanyIds = [1005, 1324, 1468];

    if (companyId === -1 || howdyCompanyIds.includes(companyId)) {
      return this.getHowdyLogoUrl(blackText);
    }

    return this.s3BaseUrl + 'assets/images/company-logos/' + companyId + '.png';
  }

  getHowdyLogoUrl(blackText = false): string {
    const suffix = blackText ? '-black' : '';

    return this.s3BaseUrl + 'assets/images/company-logos/HRONWellbeing' + suffix + '.png';
  }

  getAccountData(): Account {
    return this.accountData || JSON.parse(sessionStorage.getItem(this.ACCOUNT_KEY) || '{}');
  }

  getCompanyId(): number {
    return Number(sessionStorage.getItem(this.COMPANY_ID_KEY)) || -1;
  }

  setCompanyId(companyId: number): void {
    sessionStorage.setItem(this.COMPANY_ID_KEY, companyId.toString());
  }

  getCompanyName(): string {
    return sessionStorage.getItem(this.COMPANY_NAME_KEY) || '';
  }

  setCompanyName(companyName: string): void {
    sessionStorage.setItem(this.COMPANY_NAME_KEY, companyName);
  }

  localizeExternalHowdyLink(link: string, languageCode: string): string {
    let url = 'https://howdy.care/';

    switch (languageCode) {
        case 'dk':
            url += `dk/${link}`;
            break;
        default:
            url += link;
            break;
    }

    return url;
  }

  getTermsOfService(): Promise<string> {
    return lastValueFrom(this.httpClient.get('/api/ToS/ToS', { responseType: 'text' }));
  }

  rejectTermsOfService(): Promise<void> {
    return lastValueFrom(this.httpClient.post<void>('/api/ToS/RejectToS', null));
  }

  acceptTermsOfService(): Promise<void> {
    return lastValueFrom(this.httpClient.post<void>('/api/ToS/AcceptToS', null));
  }

  invalidPhoneNumberVerification(phoneNumber: string): Observable<string> {
    return this.httpClient.post<string>(
      '/api/Account/InvalidPhoneNumberVerification',
      null,
      {
        params: {
          phoneNumber
        }
      }
    );
  }
}
